<template>
	<div class="v-tabs">
		<div
			ref="scrollContainerRef"
			class="v-tabs__scroll"
			:style="{
				position: fixed ? 'fixed' : 'relative',
				zIndex: 11,
			}"
			@scroll="onScroll"
		>
			<div
				ref="containerRef"
				class="v-tabs__container"
				:style="{
					display: scroll ? 'inline-flex' : 'flex',
					whiteSpace: scroll ? 'nowrap' : 'normal',
					background: bgColor,
					height,
					padding,
				}"
			>
				<div
					v-for="(v, i) in tabs"
					:key="i"
					class="v-tabs__container-item"
					:style="{
						color: current === i ? activeColor : color,
						fontSize: current === i ? activeFontSize : fontSize,
						fontWeight: bold && current === i ? 'bold' : '',
						justifyContent: !scroll ? 'center' : '',
						flex: scroll ? '' : 1,
						padding: paddingItem,
					}"
					@click="handleItemClick(i)"
				>
					<a
						v-if="hrefs[i]"
						:href="hrefs[i]"
						class="v-tabs__container-link"
						@click.prevent.stop="change(i)"
					>
						<van-badge v-if="num" :content="v[num]" max="99">
							<span>{{ field ? v[field] : v }}</span>
						</van-badge>
						<template v-else>
							{{ field ? v[field] : v }}
						</template>
					</a>
					<template v-else>
						<van-badge v-if="num" :content="v[num]" max="99">
							<span>{{ field ? v[field] : v }}</span>
						</van-badge>
						<template v-else>
							{{ field ? v[field] : v }}
						</template>
					</template>
				</div>
				<div
					v-if="!pills"
					:class="['v-tabs__container-line', { animation: lineAnimation }]"
					:style="{
						background: lineColor,
						width: lineWidth + 'px',
						height: lineHeight,
						borderRadius: lineRadius,
						left: lineLeft + 'px',
						transform: `translateX(-${lineWidth / 2}px)`,
					}"
				/>
				<div
					v-else
					:class="['v-tabs__container-pills', { animation: lineAnimation }]"
					:style="{
						background: pillsColor,
						borderRadius: pillsBorderRadius,
						left: pillsLeft + 'px',
						width: currentWidth + 'px',
						height,
					}"
				/>
			</div>
		</div>
		<div
			class="v-tabs__placeholder"
			:style="{
				height: fixed ? height : '0',
				padding,
			}"
		/>
	</div>
</template>

<script setup>
/**
 * v-tabs (Vue3 原生 + Vant)
 * @property {Number} modelValue 选中的下标
 * @property {Array} tabs tabs 列表
 * @property {String} bgColor 背景颜色
 * @property {String} color 默认颜色
 * @property {String} activeColor 选中文字颜色
 * @property {String} fontSize 默认文字大小
 * @property {String} activeFontSize 选中文字大小
 * @property {Boolean} bold 选中文字是否加粗
 * @property {Boolean} scroll 是否横向滚动
 * @property {String} height tab 高度
 * @property {String} lineHeight 下划线高度
 * @property {String} lineColor 下划线颜色
 * @property {Number} lineScale 下划线宽度缩放比例
 * @property {String} lineRadius 下划线圆角
 * @property {Boolean} pills 是否胶囊样式
 * @property {String} pillsColor 胶囊背景色
 * @property {String} pillsBorderRadius 胶囊圆角
 * @property {String} field 对象时显示的键名
 * @property {Boolean} fixed 是否固定
 * @property {String} paddingItem 选项边距
 * @property {Boolean} lineAnimation 下划线动画
 * @event change(current) 切换标签
 */
import { ref, watch, onMounted, nextTick } from 'vue';

const props = defineProps({
	modelValue: { type: Number, default: 0 },
	tabs: { type: Array, default: () => [] },
	bgColor: { type: String, default: '#fff' },
	padding: { type: String, default: '0' },
	color: { type: String, default: '#333' },
	activeColor: { type: String, default: '#2979ff' },
	fontSize: { type: String, default: '1.17rem' },
	activeFontSize: { type: String, default: '1.17rem' },
	bold: { type: Boolean, default: true },
	scroll: { type: Boolean, default: true },
	height: { type: String, default: '3.67rem' },
	lineColor: { type: String, default: '#2979ff' },
	lineHeight: { type: String, default: '0.25rem' },
	lineScale: { type: Number, default: 0.5 },
	lineRadius: { type: String, default: '0.25rem' },
	pills: { type: Boolean, default: false },
	pillsColor: { type: String, default: '#2979ff' },
	pillsBorderRadius: { type: String, default: '0.25rem' },
	field: { type: String, default: '' },
	num: { type: String, default: '' },
	fixed: { type: Boolean, default: false },
	paddingItem: { type: String, default: '0 1rem' },
	lineAnimation: { type: Boolean, default: true },
	hrefs: { type: Array, default: () => [] },
});

const emit = defineEmits(['update:modelValue', 'change']);

const scrollContainerRef = ref(null);
const containerRef = ref(null);
const lineWidth = ref(60);
const currentWidth = ref(0);
const lineLeft = ref(0);
const pillsLeft = ref(0);
const scrollLeftTarget = ref(0);
const containerWidth = ref(0);
const current = ref(props.modelValue);

function randomString(len = 32) {
	const chars = 'ABCDEFGHJKMNPQRSTWXYZabcdefhijkmnprstwxyz2345678';
	let pwd = '';
	for (let i = 0; i < len; i++) {
		pwd += chars.charAt(Math.floor(Math.random() * chars.length));
	}
	return pwd;
}

function change(index) {
	if (current.value === index) return;
	current.value = index;
	emit('update:modelValue', index);
	emit('change', index);
}

/** 处理标签点击：存在可见链接时由锚点承接跳转语义，否则沿用原有切换逻辑。
 * @param {number} index 当前点击的标签索引
 * @return {void}
 */
function handleItemClick(index) {
	if (props.hrefs[index]) return;
	change(index);
}

function getTabItemWidth() {
	nextTick(() => {
		const scrollEl = scrollContainerRef.value;
		const containerEl = containerRef.value;
		if (!scrollEl || !containerEl) return;

		const scrollRect = scrollEl.getBoundingClientRect();
		if (!containerWidth.value) containerWidth.value = scrollRect.width;

		const items = containerEl.querySelectorAll('.v-tabs__container-item');
		if (!items.length) return;

		let lineLeftSum = 0;
		let currentItemWidth = 0;
		for (let i = 0; i < items.length; i++) {
			const rect = items[i].getBoundingClientRect();
			if (i < current.value) {
				lineLeftSum += rect.width;
			} else if (i === current.value) {
				currentItemWidth = rect.width;
				break;
			}
		}

		currentWidth.value = currentItemWidth;
		lineWidth.value = currentItemWidth * props.lineScale;
		lineLeft.value = lineLeftSum + currentItemWidth / 2;
		pillsLeft.value = lineLeftSum;
		if (props.scroll) {
			scrollLeftTarget.value = lineLeft.value - containerWidth.value / 2;
			scrollEl.scrollLeft = Math.max(0, scrollLeftTarget.value);
		}
	});
}

function onScroll() {
	// 可在此做滚动同步，当前由 getTabItemWidth 设置 scrollLeft 即可
}

watch(
	() => props.modelValue,
	val => {
		current.value = val;
		getTabItemWidth();
	},
	{ immediate: false }
);
watch(current, val => {
	emit('update:modelValue', val);
});
watch(
	() => props.tabs,
	() => getTabItemWidth(),
	{ deep: true }
);

onMounted(() => {
	current.value = props.modelValue;
	getTabItemWidth();
});
</script>

<style lang="scss" scoped>
.v-tabs {
	width: 100%;
	box-sizing: border-box;
	overflow: hidden;

	::-webkit-scrollbar {
		display: none;
	}

	&__scroll {
		overflow-x: auto;
		overflow-y: hidden;
		-webkit-overflow-scrolling: touch;
	}

	&__container {
		min-width: 100%;
		position: relative;
		display: inline-flex;
		align-items: center;
		white-space: nowrap;
		overflow: hidden;

		&-item {
			display: flex;
			align-items: center;
			height: 100%;
			position: relative;
			z-index: 10;
			// padding: 0 11px;
			transition: all 0.3s;
			white-space: nowrap;
			cursor: pointer;
		}

		&-link {
			display: flex;
			align-items: center;
			justify-content: inherit;
			width: 100%;
			height: 100%;
			color: inherit;
			text-decoration: none;
		}

		&-line {
			position: absolute;
			bottom: 0;
		}

		&-pills {
			position: absolute;
			z-index: 9;
		}

		&-line,
		&-pills {
			&.animation {
				transition: all 0.2s linear;
			}
		}
	}
}

:deep(.van-badge__wrapper) {
	position: relative;
}
:deep(.van-badge) {
	left: 2.2rem;
	top: -0.6rem !important;
}

.v-tabs ::-webkit-scrollbar {
	display: none;
}
.v-tabs__container {
	min-width: 100%;
	position: relative;
	display: inline-flex;
	align-items: center;
	white-space: nowrap;
	overflow: hidden;
}
.v-tabs__container-item {
	display: flex;
	align-items: center;
	height: 100%;
	position: relative;
	z-index: 10;
	transition: all 0.3s;
	white-space: nowrap;
	font-size: 1.17rem;
	font-weight: 500;
	justify-content: center;
	flex: 1 1 0%;
}
.v-tabs__container-line {
	position: absolute;
	bottom: 0;
}
.v-tabs__container-pills {
	position: absolute;
	z-index: 9;
}
.v-tabs__container-line.animation,
.v-tabs__container-pills.animation {
	transition: all 0.2s linear;
}
</style>
